// Native & external imports
import {
  Component,
  OnInit,
  OnDestroy,
  Input,
  Output,
  EventEmitter,
  Renderer2,
  inject,
} from "@angular/core";
import {
  UntypedFormBuilder,
  Validators,
  UntypedFormGroup,
} from "@angular/forms";
import { Subscription } from "rxjs";
import { debounceTime } from "rxjs/operators";
import { SubSink } from "subsink";
import { CmpSmsOptInCopy } from 'src/objects/cmp-user.object';
import { CmpAuthService, ApiCallsService } from "icamppro-shared-components";
// Services
import { AppRouterService } from "src/routing/routes-service";
import { LayoutService } from "src/services/layout.service";
import { CmpAppSettings } from "src/services/cmp-app-settings.service";
import { HelperService } from "src/services/helper.service";
// Objects
import { CmpFamilyPasswordValidationObject } from "src/objects/cmp-family-personal-info.object";
import { CmpAlertModalObject } from "src/objects/cmp-alert.object";
import { CmpGenderList } from "src/objects/cmp-gender-list.object";
import {
  CmpFauxDropdownOptionsObject,
  CmpSchoolMiniObject,
  CmpStaffingSeasonObject,
  CmpStaffApplicationTypeObject,
} from "src/objects/cmp-staff.object";
import {
  CmpFauxMetaDropDownObject,
  CmpFauxMetaDropDownOptionsObject,
  CmpFauxDropDownObject,
  CmpFauxDropDownOptionsObject,
} from "src/objects/cmp-faux-dropdown.object";
import { LocalStorageService } from "src/services/local-storage.service";
import { TokenDataObject } from "@iclassproinc/icamppro-shared-components";

@Component({
  standalone: false,
  selector: "cmp-camper-registration",
  templateUrl: "./camper-registration.component.html",
  styles: [`.consumer-type-static:after { content: none; }`],
})
export class CamperRegistrationComponent implements OnInit, OnDestroy {
  private formBuilder = inject(UntypedFormBuilder);
  private routerService = inject(AppRouterService);
  private authService = inject(CmpAuthService);
  private apiService = inject(ApiCallsService);
  layoutService = inject(LayoutService);
  private cmpAppSettings = inject(CmpAppSettings);
  helperService = inject(HelperService);
  private localStorageService = inject(LocalStorageService);
  private renderer = inject(Renderer2);

  // PUBLIC VARIABLE
  public registrationForm!: UntypedFormGroup;
  public registrationFormErrors: { [index: string]: string } = {};
  public passwordValidator: CmpFamilyPasswordValidationObject;
  public showPasswordValidation: boolean;
  public genderList: Array<{ key: string; value: string }> = [];
  public companyName = "";
  private customerCareNumber = "--";
  public loading: { [index: string]: boolean } = {};
  public classNames: { [index: string]: boolean };
  public states: { [index: string]: { [index: string]: string } } = {
    "United States": {},
  };
  public countries: any;
  public showModal: { [index: string]: boolean } = {};
  public alertModalObject = new CmpAlertModalObject();
  public smsTermsAndConditionsUrl = "";
  public referrerUrl = window.document.referrer;
  public staffingSeasons: Array<CmpStaffingSeasonObject> = [];
  public staffSettings: { [index: string]: string };
  public schools: Array<CmpSchoolMiniObject> = [];
  public defaultConsumerType = "camper";
  public showAlertModal = false;
  public showFauxDropdown = true;
  public selectedInstitutionId = 0;
  public customInstitutionName = "";
  public termsAndCondition: { [index: string]: string | boolean } = {};
  public alertModalTermsAndCondition: CmpAlertModalObject =
    new CmpAlertModalObject();
  public options: CmpFauxDropdownOptionsObject =
    new CmpFauxDropdownOptionsObject();
  public disableSchoolSearchField = false;
  public smsOptInPrompt = '';
  public smsOptInExplanation = '';
  public smsOptInExplanationHtml = '';
  public smsOptInExplanationInline = false;
  private _subSink = new SubSink();
  private _destroyed = false;
  // PRIVATE VARIABLE
  private subscription!: Subscription;
  public staffApplicationTypes: Array<CmpStaffApplicationTypeObject> = [];
  public applicationTypesDropDown: CmpFauxMetaDropDownObject =
    new CmpFauxMetaDropDownObject();
  private previousInstitutionState = false;
  public errorCase = {
    wordLength: {
      key: "wordLength",
      icon: "icon-check-circle-1",
      color: "fg-typography-subdued",
      additionalData: "Use 8 or more characters",
    },
    number: {
      key: "number",
      icon: "icon-check-circle-1",
      color: "fg-typography-subdued",
      additionalData: "Use a number",
    },
    case: {
      key: "case",
      icon: "icon-check-circle-1",
      color: "fg-typography-subdued",
      additionalData: "Use upper and lower case letters",
    },
    symbol: {
      key: "symbol",
      icon: "icon-check-circle-1",
      color: "fg-typography-subdued",
      additionalData: "Use a symbol (e.g. !@#$)",
    },
    check: {
      key: "check",
      icon: "icon-check-circle-1",
      color: "fg-typography-subdued",
      additionalData: "",
    },
  };
  public errorCaseConfirmPassword = {
    check: {
      key: "check",
      icon: "icon-check-circle-1",
      color: "fg-typography-subdued",
      additionalData: "",
    },
  };
  public gendersList: CmpFauxDropDownObject = new CmpFauxDropDownObject();
  public consumerTypesList: CmpFauxDropDownObject = new CmpFauxDropDownObject();
  public staffingSeasonsList: CmpFauxDropDownObject =
    new CmpFauxDropDownObject();
  public statesList: CmpFauxDropDownObject = new CmpFauxDropDownObject();
  public countriesList: CmpFauxDropDownObject = new CmpFauxDropDownObject();
  public selectedState = "";
  public spaceSize = "0rem";
  public showSpace = false;
  public schoolError = false;
  public isMobile = false;
  public controlsObj = {
    isFormValid: false,
    isAttendingSchool: true,
    isCustomSchool: false,
  };
  private passwordPattern =
    /(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*#^?&])[A-Za-z0-9d$@$!%*#^?&].{7,}/;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private previousSelectedInput = {
    passwordViewFocusCount: 0,
    confirmPasswordViewFocusCount: 0,
  };

  // Input VARIABLE
  @Input() newAccountDetails: {
    email: string;
    firstName: string;
    lastName: string;
  } = { email: "", firstName: "", lastName: "" };
  // Output VARIABLE
  @Output() emitResponse = new EventEmitter();

  /**
   * @param formBuilder UntypedFormBuilder
   * @param routerService AppRouterService
   * @param authService CmpAuthService
   * @param apiService ApiCallsService
   * @param titleCasePipe TitleCasePipe
   * @param layoutService LayoutService
   * @return void
   */
  constructor() {
    this.isMobile = window.innerWidth < 768;
    const genderListObject = new CmpGenderList();
    this.genderList = genderListObject.getBasicGenderList();
    this.showPasswordValidation = false;
    this.passwordValidator = {
      wordLength: false,
      number: false,
      case: false,
      symbol: false,
      edit: false,
      validationPassed: false,
      editConfirmPassword: false,
      mustMatch: false,
      finalMatchedPassword: false,
    };
    this.initiateFormErrors();
    this.classNames = {
      emailFocus: false,
      firstNameFocus: false,
      lastNameFocus: false,
      passwordFocus: false,
      confirmPasswordFocus: false,
      addressFocus: false,
      cityFocus: false,
      homePhoneFocus: false,
      cellPhoneFocus: false,
    };
    this.smsTermsAndConditionsUrl =
      this.cmpAppSettings.httpUrlFormatConvert(
        this.cmpAppSettings.getAppSetting("smsTermsAndConditionsUrl"),
      ) || "";
  }

  /**
   * Set sidebar menu names and initialize form values
   * Change the firstName and lastName to titleCase on every change
   * @return void
   */
  ngOnInit() {
    this._subSink.sink = this.helperService.smsOptInCopy$.subscribe(
      (copy: CmpSmsOptInCopy) => {
        this.smsOptInPrompt = copy.prompt;
        this.smsOptInExplanation = copy.explanation;
        this.smsOptInExplanationHtml = copy.explanationHtml;
        this.smsOptInExplanationInline = copy.inline;
      },
    );

    Promise.resolve().then(() => {
      if (!this._destroyed) {
        this.layoutService.createAccountPage = false;
        this.layoutService.hasActionBarClass = true;
        this.layoutService.noBackground = true;
        this.layoutService.isAccountCreationLoader = false;
      }
    });
    this.helperService.pushDataLayer(
      "create-account-complete",
      "Create Account - Complete",
    );
    this.registrationForm = this.formBuilder.group({
      email: [this.newAccountDetails.email ? this.newAccountDetails.email : ""],
      firstName: [
        this.newAccountDetails.firstName
          ? this.newAccountDetails.firstName
          : "",
      ],
      lastName: [
        this.newAccountDetails.lastName ? this.newAccountDetails.lastName : "",
      ],
      gender: [""],
      adult: [""],
      password: [null, Validators.required],
      confirmPassword: [null, Validators.required],
      address: [""],
      city: [""],
      state: [""],
      postalCode: [""],
      country: ["United States"],
      homePhone: [null],
      cellPhone: [null],
      optedIn: [false],
      hasOptedInForPromotionalEmail: [false],
      defaultConsumerType: [""],
      customInstitutionName: [null],
      staffingSeasonId: [""],
      institutionType: [""],
      staffCellPhone: [""],
      termsAndCondition: [""],
      selectedInstitutionId: [null],
      applicationTypeId: [null],
      confirmationPrompt: [""],
      confirmationCheckbox: [false],
      homeOptedIn: [false],
    });
    this.loadCountryStates();
    this.frameGendersList();
    this.helperService.loadTwilioSetting();
    if (this.newAccountDetails.email) {
      this.addRemoveClass(this.registrationForm, "", "email");
      this.addRemoveClass(this.registrationForm, "", "firstName");
      this.addRemoveClass(this.registrationForm, "", "lastName");
    }
    this.subscription = this.registrationForm.valueChanges
      .pipe(debounceTime(50))
      .subscribe((value) => {
        if (value.firstName !== "") {
          // value.firstName = this.titleCasePipe.transform(value.firstName);
          this.fieldsValidation("firstName");
        }
        if (value.lastName !== "") {
          // value.lastName = this.titleCasePipe.transform(value.lastName);
          this.fieldsValidation("lastName");
        }
        this.validatePassword(value.password);
        this.validateConfirmPassword(value.confirmPassword);
      });
    window.scrollTo(0, 0);
    this.companyName = this.cmpAppSettings.getAppSetting("companyName");
    this.customerCareNumber = this.cmpAppSettings.getAppSetting(
      "customerServicePhoneNumber",
    );
    this.getStaffModuleSettings();
    this.getTermsAndConditions();
    this.getStaffingSeason();
  }

  ngOnDestroy() {
    this._destroyed = true;
    this.subscription.unsubscribe();
    this._subSink.unsubscribe();
    this.layoutService.hasActionBarClass = false;
    this.layoutService.createAccountPage = true;
    this.isMobile = false;
  }

  public validateForm(fieldName?: string) {
    const staticControls = [
      "email",
      "firstName",
      "lastName",
      "gender",
      "password",
      "confirmPassword",
      "defaultConsumerType",
    ];
    const camperControls = [
      "address",
      "city",
      "state",
      "postalCode",
      "country",
      "adult",
    ];
    const staffControls = ["staffingSeasonId", "applicationTypeId"];

    const appTypeId = this.registrationForm.controls.applicationTypeId.value;
    const hasSchoolSelection =
      !!appTypeId &&
      !!this.staffApplicationTypes.find((f) => f.id === +appTypeId)
        ?.schoolSelection;
    if (hasSchoolSelection && this.controlsObj.isAttendingSchool) {
      if (this.controlsObj.isCustomSchool) {
        staffControls.push("customInstitutionName");
      } else {
        staffControls.push("selectedInstitutionId");
      }
    }
    const confirmationCheckbox =
      this.registrationForm.controls["confirmationPrompt"].value;
    if (confirmationCheckbox) {
      staffControls.push("confirmationCheckbox");
    }

    const institutionType =
      this.registrationForm.controls["institutionType"].value;
    if (institutionType) {
      staffControls.push("staffCellPhone");
      if (this.termsAndCondition.show) {
        staffControls.push("termsAndCondition");
      }
    }

    const controls = this.registrationForm.controls;
    const defaultConsumerType = controls.defaultConsumerType.value;
    const cellPhoneValue = controls.cellPhone.value;
    const homePhoneValue = controls.homePhone.value;
    const phoneValue: string = cellPhoneValue
      ? cellPhoneValue
      : controls.homePhone.value;

    const emailIsValid = this.helperService
      .emailPattern()
      .test(controls.email.value ?? "");
    const isStaticControlsValid = staticControls.every(
      (e) => !!controls[e].value,
    );
    const isPhoneValid =
      !!phoneValue &&
      phoneValue.length === 14 &&
      (!cellPhoneValue || cellPhoneValue.length === 14) &&
      (!homePhoneValue || homePhoneValue.length === 14);
    const isCamperControlsValid =
      defaultConsumerType !== "camper" ||
      (camperControls.every((e) => !!controls[e].value) && isPhoneValid);
    const isStaffPhoneValid =
      !staffControls.includes("staffCellPhone") ||
      (!!controls.staffCellPhone.value &&
        controls.staffCellPhone.value.length === 14);
    const isStaffControlsValid =
      defaultConsumerType !== "staff" ||
      (staffControls.every((e) => !!controls[e].value) && isStaffPhoneValid);
    const isPasswordValid =
      this.passwordPattern.test(controls.password.value ?? "") &&
      controls.password.value === controls.confirmPassword.value;

    if (fieldName) {
      delete this.registrationFormErrors[fieldName];
    }

    // Check for any error messages in registrationFormErrors
    const hasValidationErrors = Object.values(this.registrationFormErrors).some(
      (msg) => !!msg,
    );

    this.controlsObj.isFormValid =
      emailIsValid &&
      isStaticControlsValid &&
      isPasswordValid &&
      isCamperControlsValid &&
      isStaffControlsValid &&
      !hasValidationErrors;
  }

  public fieldsValidation(fieldName: string) {
    const formValue = this.registrationForm.value;
    switch (fieldName) {
      case "email":
        {
          const emailIsValid = this.helperService
            .emailPattern()
            .test(formValue.email ?? "");
          this.registrationFormErrors.email =
            !!formValue.email && !emailIsValid ? "Enter a valid email" : "";
        }
        break;
      case "firstName":
        {
          const value = this.registrationForm.controls["firstName"].value;
          if (!!value && value.length > 40) {
            this.registrationFormErrors.firstName =
              "The first name must not be greater than 40 characters.";
          } else {
            this.registrationFormErrors.firstName = "";
          }
        }
        break;
      case "lastName":
        {
          const value = this.registrationForm.controls["lastName"].value;
          if (!!value && value.length > 40) {
            this.registrationFormErrors.lastName =
              "The last name must not be greater than 40 characters.";
          } else {
            this.registrationFormErrors.lastName = "";
          }
        }
        break;
      case "homePhone":
      case "cellPhone":
      case "staffCellPhone":
        {
          const friendlyName: { [index: string]: string } = {
            cellPhone: "Cell phone",
            staffCellPhone: "Cell phone",
            homePhone: "Home phone",
          };
          const value = this.registrationForm.controls[fieldName].value;
          const minError = !!value && value.length < 14;
          this.registrationFormErrors[fieldName] = minError
            ? `${friendlyName[fieldName]} is too short`
            : "";
        }
        break;
      case "password":
        {
          const passwordIsValid = this.passwordPattern.test(
            formValue.password ?? "",
          );
          this.registrationFormErrors.password =
            !!formValue.password && !passwordIsValid
              ? "Password is not in the proper format"
              : "";
        }
        break;
      case "confirmPassword":
        {
          const passwordMatches =
            formValue.password === formValue.confirmPassword;
          this.registrationFormErrors.confirmPassword =
            !!formValue.confirmPassword && !passwordMatches
              ? "Confirm password does not match"
              : "";
        }
        break;
    }
  }

  /**
   * @method initiateFormErrors
   * @description Initialize form errors with empty values
   * @returns void
   */
  private initiateFormErrors() {
    this.registrationFormErrors = {
      firstName: "",
      lastName: "",
      gender: "",
      email: "",
      password: "",
      confirmPassword: "",
      adult: "",
      address: "",
      address2: "",
      city: "",
      state: "",
      postalCode: "",
      country: "",
      phone: "",
      defaultConsumerType: "",
      staffingSeasonId: "",
      institutionType: "",
      staffCellPhone: "",
      termsAndCondition: "",
      selectedInstitutionId: "",
      confirmationPrompt: "",
      confirmationCheckbox: "",
    };
  }

  /**
   * @method validatePassword
   * @description Show/Hide Password validation texts
   * @param password string
   * @returns void
   */
  private validatePassword(password: string) {
    if (password) {
      this.passwordValidator.wordLength = password.length > 7 ? true : false;
      this.passwordValidator.case =
        /[A-Z]/.test(password) && /[a-z]/.test(password) ? true : false;
      this.passwordValidator.number = /[0-9]/.test(password) ? true : false;
      this.passwordValidator.symbol = /[$@$!%*#^?&]/.test(password)
        ? true
        : false;
      this.passwordValidator.validationPassed =
        this.passwordValidator.wordLength &&
        this.passwordValidator.case &&
        this.passwordValidator.number &&
        this.passwordValidator.symbol;
      this.errorCaseHandle();
    } else {
      this.passwordValidator.wordLength = false;
      this.passwordValidator.case = false;
      this.passwordValidator.number = false;
      this.passwordValidator.symbol = false;
      this.passwordValidator.validationPassed = false;
      this.errorCaseHandle();
    }
  }

  /**
   * @method validateConfirmPassword
   * @description Show/Hide Confirm Password validation texts
   */
  private validateConfirmPassword(confirmPassword: string) {
    if (confirmPassword) {
      const mustMatch = this.helperService.mustMatch(this.registrationForm);
      if (mustMatch !== null && confirmPassword !== "") {
        if (!this.passwordValidator.editConfirmPassword) {
          this.registrationForm.controls["confirmPassword"].setErrors(null);
          this.passwordValidator.mustMatch = false;
        } else this.passwordValidator.mustMatch = true;
        this.passwordValidator.finalMatchedPassword = true;
        this.errorCaseConfirmPassword["check"].icon = this.passwordValidator
          .editConfirmPassword
          ? "icon-delete-1"
          : "icon-check-circle-1";
        this.errorCaseConfirmPassword["check"].color = this.passwordValidator
          .editConfirmPassword
          ? "fg-danger"
          : "fg-typography-subdued";
      } else {
        this.errorCaseConfirmPassword["check"].icon = "icon-check-circle-1";
        this.errorCaseConfirmPassword["check"].color = "fg-success";
        this.passwordValidator.mustMatch = false;
        this.passwordValidator.finalMatchedPassword = false;
      }
    } else {
      if (this.passwordValidator.editConfirmPassword) {
        this.errorCaseConfirmPassword["check"].icon = "icon-delete-1";
        this.errorCaseConfirmPassword["check"].color = "fg-danger";
        this.passwordValidator.mustMatch = true;
      }
      this.passwordValidator.finalMatchedPassword = true;
    }
  }

  /**
   * @method errorCaseHandle
   * @description Show/Hide Password validation texts
   */
  private errorCaseHandle() {
    this.errorCase["wordLength"].color = this.passwordValidator.wordLength
      ? "fg-success"
      : this.passwordValidator.edit
        ? "fg-danger"
        : "fg-typography-subdued";
    this.errorCase["case"].color = this.passwordValidator.case
      ? "fg-success"
      : this.passwordValidator.edit
        ? "fg-danger"
        : "fg-typography-subdued";
    this.errorCase["number"].color = this.passwordValidator.number
      ? "fg-success"
      : this.passwordValidator.edit
        ? "fg-danger"
        : "fg-typography-subdued";
    this.errorCase["symbol"].color = this.passwordValidator.symbol
      ? "fg-success"
      : this.passwordValidator.edit
        ? "fg-danger"
        : "fg-typography-subdued";
    this.errorCase["check"].color = this.passwordValidator.validationPassed
      ? "fg-success"
      : this.passwordValidator.edit
        ? "fg-danger"
        : "fg-typography-subdued";
    this.errorCase["wordLength"].icon = this.passwordValidator.wordLength
      ? "icon-check-circle-1"
      : this.passwordValidator.edit
        ? "icon-delete-1"
        : "icon-check-circle-1";
    this.errorCase["case"].icon = this.passwordValidator.case
      ? "icon-check-circle-1"
      : this.passwordValidator.edit
        ? "icon-delete-1"
        : "icon-check-circle-1";
    this.errorCase["number"].icon = this.passwordValidator.number
      ? "icon-check-circle-1"
      : this.passwordValidator.edit
        ? "icon-delete-1"
        : "icon-check-circle-1";
    this.errorCase["symbol"].icon = this.passwordValidator.symbol
      ? "icon-check-circle-1"
      : this.passwordValidator.edit
        ? "icon-delete-1"
        : "icon-check-circle-1";
    this.errorCase["check"].icon = this.passwordValidator.validationPassed
      ? "icon-check-circle-1"
      : this.passwordValidator.edit
        ? "icon-delete-1"
        : "icon-check-circle-1";
  }

  /**
   * @method addRemoveClass
   * @description add or remove the input-focused class
   * @param formGroup UntypedFormGroup
   * @param action string
   * @param element string
   * @returns response callback
   */
  public addRemoveClass(
    formGroup: UntypedFormGroup,
    action = "",
    element = "",
    event?: any,
  ) {
    const clickedIcon =
      event &&
      event.relatedTarget &&
      event.relatedTarget.classList.contains("view-toggle-2-icons");
    const viewElement =
      event && event.relatedTarget ? event.relatedTarget?.id : 0;
    switch (element) {
      case "email":
        this.classNames.emailFocus =
          action === "focus"
            ? true
            : formGroup.controls["email"].value
              ? true
              : false;
        break;
      case "firstName":
        this.classNames.firstNameFocus =
          action === "focus"
            ? true
            : formGroup.controls["firstName"].value
              ? true
              : false;
        break;
      case "lastName":
        this.classNames.lastNameFocus =
          action === "focus"
            ? true
            : formGroup.controls["lastName"].value
              ? true
              : false;
        break;
      case "password":
        if (action === "focus") {
          this.classNames.passwordFocus = true;
          this.showPasswordValidation = true;
        } else {
          if (clickedIcon && viewElement === "viewPassword") {
            this.viewPasswordValidator(
              element,
              this.previousSelectedInput.passwordViewFocusCount,
              formGroup,
            );
          } else {
            this.classNames.passwordFocus = formGroup.controls["password"].value
              ? true
              : false;
            if (formGroup.controls["password"].value === null) {
              formGroup.controls["password"].setErrors({ required: true });
            }
            this.previousSelectedInput.passwordViewFocusCount = 1;
            this.helperService.changeFormField(
              this.registrationForm,
              "password",
              false,
              "",
              true,
              [
                Validators.required,
                Validators.pattern(
                  "(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*#^?&])[A-Za-z0-9d$@$!%*#^?&].{7,}",
                ),
              ],
            );
            this.passwordValidator.edit = true;
            this.validatePassword(formGroup.controls["password"].value);
            if (this.passwordValidator.validationPassed) {
              this.showPasswordValidation = false;
              if (clickedIcon && viewElement === "viewConfirmPassword") {
                this.helperService.viewPasswordToggle(
                  "viewConfirmPassword",
                  "confirmPassword",
                );
                this.renderer.selectRootElement("#confirmPassword").focus();
              }
            }
          }
        }
        break;
      case "confirmPassword":
        if (action !== "focus") {
          if (clickedIcon && viewElement === "viewConfirmPassword") {
            this.viewPasswordValidator(
              element,
              this.previousSelectedInput.confirmPasswordViewFocusCount,
              formGroup,
            );
          } else {
            this.classNames.confirmPasswordFocus = formGroup.controls[
              "confirmPassword"
            ].value
              ? true
              : false;
            if (formGroup.controls["confirmPassword"].value === null) {
              formGroup.controls["confirmPassword"].setErrors({
                required: true,
              });
            }
            this.previousSelectedInput.confirmPasswordViewFocusCount = 1;
            this.passwordValidator.editConfirmPassword = true;
            this.validateConfirmPassword(
              formGroup.controls["confirmPassword"].value,
            );
          }
        } else {
          this.classNames.confirmPasswordFocus = true;
        }
        break;
      case "address":
        this.classNames.addressFocus =
          action === "focus"
            ? true
            : formGroup.controls["address"].value
              ? true
              : false;
        break;
      case "city":
        this.classNames.cityFocus =
          action === "focus"
            ? true
            : formGroup.controls["city"].value
              ? true
              : false;
        break;
      case "postalCode":
        this.classNames.postalCodeFocus =
          action === "focus"
            ? true
            : formGroup.controls["postalCode"].value
              ? true
              : false;
        break;
      case "homePhone":
        this.classNames.homePhoneFocus =
          action === "focus"
            ? true
            : formGroup.controls["homePhone"].value
              ? true
              : false;
        break;
      case "cellPhone":
        this.classNames.cellPhoneFocus =
          action === "focus"
            ? true
            : formGroup.controls["cellPhone"].value
              ? true
              : false;
        break;
      case "staffCellPhone":
        this.classNames.staffCellPhoneFocus =
          action === "focus"
            ? true
            : formGroup.controls["staffCellPhone"].value
              ? true
              : false;
        break;
      case "defaultConsumerType":
        this.classNames.defaultConsumerTypeFocus =
          action === "focus"
            ? true
            : formGroup.controls["defaultConsumerType"].value
              ? true
              : false;
        break;
      case "staffingSeasonId":
        this.classNames.staffingSeasonIdFocus =
          action === "focus"
            ? true
            : formGroup.controls["staffingSeasonId"].value
              ? true
              : false;
        break;
      case "institutionType":
        this.classNames.institutionTypeFocus =
          action === "focus"
            ? true
            : formGroup.controls["institutionType"].value
              ? true
              : false;
        break;
      case "applicationTypeId":
        this.classNames.applicationTypeIdFocus =
          action === "focus"
            ? true
            : formGroup.controls["applicationTypeId"].value
              ? true
              : false;
        break;
    }
  }

  /**
   * @method viewPasswordValidator
   * @description Hide/View password validation based on icon change
   * @returns void
   */
  private viewPasswordValidator(
    field: string,
    count: number,
    formGroup: UntypedFormGroup,
  ) {
    if (formGroup.controls[field].value === null && count === 0) {
      formGroup.controls[field].setErrors(null);
    } else if (formGroup.controls[field].value === null && count === 1) {
      formGroup.controls[field].setErrors({ required: true });
    }
    if (field === "password") {
      this.classNames.passwordFocus = true;
    } else if (field === "confirmPassword") {
      this.classNames.confirmPasswordFocus = true;
    }
  }

  /**
   * @method viewPassword
   * @description Hide/View password icon
   * @returns void
   */
  public viewPassword(field: string) {
    if (field === "confirmPassword") {
      this.renderer.selectRootElement("#confirmPassword").focus();
    } else if (field === "password") {
      this.renderer.selectRootElement("#password").focus();
    }
  }

  /**
   * @method checkTabPress
   * @description password validation on triggering tab event
   * @returns void
   */
  public checkTabPress(field: string, event: any) {
    if (field === "password") {
      this.addRemoveClass(this.registrationForm, "blur", "password", event);
      this.addRemoveClass(
        this.registrationForm,
        "focus",
        "confirmPassword",
        event,
      );
    } else if (field === "confirmPassword") {
      this.addRemoveClass(
        this.registrationForm,
        "blur",
        "confirmPassword",
        event,
      );
      this.addRemoveClass(this.registrationForm, "focus", "address");
    }
  }

  /**
   * @method loadCountryStates
   * @description it loads the states list of the United State.
   * @returns void
   */
  private loadCountryStates() {
    this.loading.countries = true;
    this.apiService
      .get(this.routerService.backendApiUrl.getStates)
      .subscribe((response) => {
        if (!response.hasErrors) {
          this.countries = new Map(
            Object.entries(response.data).map(([country, states]) => {
              // Replace empty array with object containing the country itself
              if (Array.isArray(states) && states.length === 0) {
                return [country, { "01": country }];
              }

              // If states is an object, check keys and prepend '0' if key length is 1
              if (
                states &&
                typeof states === "object" &&
                !Array.isArray(states)
              ) {
                const updatedStateKeys = Object.entries(states).reduce(
                  (acc, [key, value]) => {
                    const newKey = key.length === 1 ? "0" + key : key;
                    acc[newKey] = value;
                    return acc;
                  },
                  {} as { [key: string]: string },
                );
                return [country, updatedStateKeys];
              }

              return [country, states];
            }),
          );
          const options: Array<CmpFauxDropDownOptionsObject> = [];
          this.countries.forEach((item: any, key: any) => {
            const option = new CmpFauxDropDownOptionsObject();
            option.value = key;
            option.name = key;
            options.push(option);
          });
          this.countriesList.options = options;
          this.countriesList.label.name = "Country";
          this.countriesList.label.defaultText = "Country";
          this.countriesList.label.selectedLabel = "United States";
          this.states = this.countries.get(
            this.registrationForm.controls["country"].value,
          );
          this.frameStatesList();
        }
        this.loading.countries = false;
      });
  }

  /**
   * @method frameStatesList
   * @description it frame the states list.
   * @returns void
   */
  private frameStatesList() {
    const options: Array<CmpFauxDropDownOptionsObject> = [];
    for (const key in this.states) {
      const stateName = this.states[key];
      if (stateName !== null && stateName !== undefined) {
        const option = new CmpFauxDropDownOptionsObject();
        option.value = key;
        option.name =
          typeof stateName === "string" ? stateName : stateName.toString();
        options.push(option);
      }
    }
    options.sort((a, b) => a.name.localeCompare(b.name));
    this.statesList.options = options;
    this.statesList.label.name = "State";
    this.statesList.label.defaultText = "State";
  }

  /**
   * @method frameGendersList
   * @description it frame the genders list.
   * @returns void
   */
  private frameGendersList() {
    const options: Array<CmpFauxDropDownOptionsObject> = [];
    this.genderList.forEach((item) => {
      const option = new CmpFauxDropDownOptionsObject();
      option.value = item.key;
      option.name = item.value;
      options.push(option);
    });
    this.gendersList.options = options;
    this.gendersList.label.name = "Gender";
    this.gendersList.label.defaultText = "Gender";
  }

  /**
   * @method createAccount
   * @description it will register new account.
   * @returns void
   */
  public createAccount() {
    if (this.loading.register) {
      return false;
    }
    this.loading.register = true;
    this.layoutService.isAccountCreationLoader = true;
    this.autoLogout(); // redirect user, when account creation api response exceed 60 sec
    const payLoad = this.registrationForm.value;
    payLoad.scope = "consumer";
    payLoad.phone = payLoad.cellPhone ? payLoad.cellPhone : payLoad.homePhone;
    payLoad.referrerUrl = this.referrerUrl;
    payLoad.defaultConsumerType = this.defaultConsumerType;
    payLoad.customInstitutionName =
      this.registrationForm.controls.customInstitutionName.value &&
      this.selectedInstitutionId === 0 &&
      !this.showFauxDropdown
        ? this.registrationForm.controls.customInstitutionName.value
        : null;
    payLoad.createdVia = "Customer Portal";
    if (this.registrationForm.controls.institutionType.value === "college") {
      payLoad.institutionId =
        this.selectedInstitutionId !== 0 && this.showFauxDropdown
          ? this.selectedInstitutionId
          : null;
    } else {
      payLoad.institutionId =
        this.registrationForm.controls.institutionType.value === "school"
          ? -1
          : -2;
    }
    this.disableFields();
    this.apiService = this.helperService.callToLoadBalancer(this.apiService);
    this._subSink.add(
      this.authService.registerCamper(payLoad).subscribe((response) => {
        if (!response.hasErrors && response.data) {
          if (response.data.consumer_type === "camper") {
            this.handleRegSuccess(response.data);
          } else {
            window.location.href = response.data.domain;
          }
        } else {
          this.handleError(response);
        }
        this.registrationForm.markAsPristine();
        this.enableFields();
      }),
    );
    this.apiService = this.helperService.revertLoadBalancer(this.apiService);
  }

  private disableFields() {
    for (const key in this.registrationForm.controls) {
      this.registrationForm.controls[key].disable();
    }
    this.previousInstitutionState = this.options.disableSearchDropdown;
    this.applicationTypesDropDown.disabled = true;
    this.options.disableClearButton = true;
    this.options.disableSearchDropdown = true;
  }

  private enableFields() {
    for (const key in this.registrationForm.controls) {
      this.registrationForm.controls[key].enable();
    }
    this.options.disableSearchDropdown = this.previousInstitutionState;
    this.options.disableClearButton = false;
    this.applicationTypesDropDown.disabled = false;
  }

  /**
   * @method handleRegSuccess
   * @description Handles the Registration success event for camper
   */
  private handleRegSuccess(data: TokenDataObject) {
    this.helperService.resetGtm();
    this.localStorageService.setItem('consumer', JSON.stringify(data));
    this.localStorageService.setItem('CMP_CONSUMER', data.access_token);
    this.localStorageService.setItem('CMP_CONSUMER_PORTAL', 'true');
    this.localStorageService.setItem('staffUserLoggedIn', 'false');
    // Flush local Storage data
    this.localStorageService.removeItems([
      "locations",
      "events",
      "eventSeries",
      "eventDate",
      "attendee",
      "subtotal",
      "tax",
      "total",
    ]);
    this.layoutService.setUserDetail();
    this.helperService.saveUtmFields = true;
    this.helperService.setMostRecentUrl();
    this.showNext("accountCreated", data);
  }

  /**
   * @method handleError
   * @param response Object<Any>
   * @description  to handle error and construct AlertModal
   * @returns void
   */
  private handleError(response: any) {
    this.loading.register = false;
    this.layoutService.isAccountCreationLoader = false;
    clearTimeout(this.timer); // clear the timeout to autologOut() method
    const alertModal = new CmpAlertModalObject();
    const emailError = response.errors.find(
      (error: { field: string; message: string }) => error.field === "email",
    );
    if (emailError && emailError.message.indexOf("registered") !== -1) {
      // Emit to parent to open alert modal
      this.showNext("openAlertModal", "");
    } else if (response.status === 500) {
      alertModal.title = "Error";
      alertModal.content = `Uh-oh! Our system is currently experiencing a technical issue. Please verify your information and click Save
        again. If you continue to receive this message, Please contact our Customer Service Team at ${this.customerCareNumber}.`;
      alertModal.buttonName = "Close";
      alertModal.buttonAction = "";
      this.openAlertModal(alertModal);
    } else if (response.status === 403) {
      alertModal.title = "Error";
      if (response.message.toLowerCase().includes("conflicting username")) {
        alertModal.content =
          "Whoops! It looks like this account already exists.";
      } else {
        alertModal.content = response.message;
      }
      alertModal.buttonName = "Close";
      alertModal.buttonAction = "";
      this.openAlertModal(alertModal);
    } else {
      response.errors.forEach((error: { field: string; message: string }) => {
        this.registrationFormErrors[error.field] = error.message;
      });
    }
  }

  /**
   * @method showNext
   * @param closePopupValue string
   * @param value string
   * @description emit to the parent component
   * @returns void
   */
  public showNext(closePopupValue = "", value: any) {
    this.emitResponse.emit({
      from: "camperRegistration",
      to: closePopupValue,
      value: value,
    });
  }

  /**
   * @method modalEmitResponse
   * @param modalResponse Object<{from: string, value: any }>
   * @description emit to the modal response
   * @returns void
   */
  public modalEmitResponse(modalResponse: { from: string; value: any }) {
    switch (modalResponse.from) {
      case "alertModal":
        if (!modalResponse.value || modalResponse.value === "Close") {
          this.showModal.alertModal = false;
          this.termsAndCondition.alert = false;
        }
        break;
    }
  }

  /**
   * @method autoLogout
   * @description redirect user to home
   * @returns void
   */
  private autoLogout() {
    this.timer = setTimeout(() => {
      this._subSink.unsubscribe();
      window.location.reload();
    }, 60000);
  }

  /**
   * @method openAlertModal
   * @description Open registration module
   * @returns void
   */
  public openAlertModal(alertModal: CmpAlertModalObject) {
    this.alertModalObject = alertModal;
    this.showModal.alertModal = true;
  }

  /**
   * @method countryOnChange
   * @description Assign state value based on country change
   * @returns void
   */
  public countryOnChange(event: any) {
    this.registrationForm.controls["country"].setValue(event.option.value);
    this.registrationForm.controls["state"].setValue("");
    this.registrationForm.controls["state"].updateValueAndValidity();
    this.registrationForm.controls["state"].markAsPristine();
    this.registrationForm.controls["state"].markAsUntouched();
    this.statesList.label.selectedLabel = "";
    this.states = { "": {} };
    this.states = this.countries.get(event.option.value);
    this.frameStatesList();
    this.validateForm();
  }

  public getStaffModuleSettings() {
    this.layoutService.getStaffModuleSettings().then((settings) => {
      if (settings) {
        this.staffSettings = settings;
        const inactiveStaffingSeasonMessage = this.staffSettings
          .inactiveStaffingSeasonMessage
          ? this.staffSettings.inactiveStaffingSeasonMessage
          : "We are not currently accepting new applications.";
        this.staffSettings.inactiveStaffingSeasonMessage = `<span class="alert-icon size-icon-medium icon-information-circle fg-typography"></span>${inactiveStaffingSeasonMessage}`;
        this.setStaffingSeasonPlaceholder();
        this.options.showCustom = settings.schoolSearch;
        this.options.showCustomInput = settings.customSchoolEntry;
        this.options.noResultText = settings.schoolTextarea;
        this.options.customInputText = settings.customSchoolPrompt;
        this.options.defaultCustomInputText =
          "Still no luck? Click here to add your school";
        this.options.label = "School";
        this.options.disableValidation = true;
        if (settings.staffPortalIntegration) {
          this.helperService.changeFormField(
            this.registrationForm,
            "defaultConsumerType",
            false,
            "",
            true,
            [Validators.required],
          );
          this.defaultConsumerType = "";

          /** Consumer type settings */
          const consumerTypesList = [
            {
              key: "camper",
              value:
                !settings.customizeAccountTypeLabels ||
                settings.registrationPortalLabel == ""
                  ? "Register for Camp"
                  : settings.registrationPortalLabel,
            },
            {
              key: "staff",
              value:
                !settings.customizeAccountTypeLabels ||
                settings.staffPortalLabel == ""
                  ? "Work at Camp"
                  : settings.staffPortalLabel,
            },
          ];
          const options: Array<CmpFauxDropDownOptionsObject> = [];
          consumerTypesList.forEach((item) => {
            const option = new CmpFauxDropDownOptionsObject();
            option.value = item.key;
            option.name = item.value;
            options.push(option);
          });
          this.consumerTypesList.options = options;
          this.consumerTypesList.label.name = "I want to...";
          this.consumerTypesList.label.defaultText = "I want to...";
        } else {
          this.helperService.changeFormField(
            this.registrationForm,
            "address",
            false,
            null,
          );
          this.helperService.changeFormField(
            this.registrationForm,
            "city",
            false,
            null,
          );
          this.helperService.changeFormField(
            this.registrationForm,
            "state",
            false,
            null,
          );
          this.helperService.changeFormField(
            this.registrationForm,
            "postalCode",
            false,
            null,
          );
          this.helperService.changeFormField(
            this.registrationForm,
            "adult",
            false,
            "",
          );
          this.helperService.changeFormField(
            this.registrationForm,
            "defaultConsumerType",
            true,
            "camper",
          );
          this.defaultConsumerType = 'camper';

          /** No staff option available - show a static, non-interactive "Register for Camp" value */
          this.consumerTypesList.label.name = "I want to...";
          this.consumerTypesList.label.selectedLabel = !settings.customizeAccountTypeLabels || settings.registrationPortalLabel == '' ? 'Register for Camp' : settings.registrationPortalLabel;
        }
      }
    });
  }

  /**
   * @method getStaffingSeason
   * @description staff seasons for staff portal registration
   * @returns void
   */
  public getStaffingSeason() {
    this._subSink.add(
      this.apiService
        .get(this.routerService.backendApiUrl.getStaffingSeason, {
          sortOnlyByName: true,
        })
        .subscribe((response) => {
          if (
            !response.hasErrors &&
            response.data &&
            Array.isArray(response.data)
          ) {
            this.staffingSeasons = response.data;
            const options: Array<CmpFauxDropDownOptionsObject> = [];
            this.staffingSeasons.forEach((item) => {
              const option = new CmpFauxDropDownOptionsObject();
              option.value = item.staffingSeasonId;
              option.name = item.staffingSeasonName;
              options.push(option);
            });
            this.staffingSeasonsList.options = options;
          this.setStaffingSeasonPlaceholder();
          }
        }),
    );
  }

  /**
   * @method setStaffingSeasonPlaceholder
   * @description Applies the admin-configured staffing season placeholder to the dropdown label, falling back to the default when none is configured.
   * @returns void
   */
  private setStaffingSeasonPlaceholder(): void {
    const placeholder = this.staffSettings && this.staffSettings.staffingSeasonPlaceholder
      ? this.staffSettings.staffingSeasonPlaceholder
      : "Select a Summer...";
    this.staffingSeasonsList.label.name = placeholder;
    this.staffingSeasonsList.label.defaultText = placeholder;
  }

  /**
   * @method getSchools
   * @description schools for staff portal registration
   * @returns void
   */
  private getSchools() {
    this._subSink.add(
      this.apiService
        .get(this.routerService.backendApiUrl.institutions)
        .subscribe((response) => {
          if (response.data) {
            this.schools = response.data.map((school: CmpSchoolMiniObject) => {
              school.miniText = school.city;
              school.miniText2 = school.stateCode;
              school.miniText3 = school.abbreviation;
              return school;
            });
            this.options.schools = this.schools;
          }
        }),
    );
  }

  public setValue(
    event: { option: CmpFauxDropDownOptionsObject },
    key: string,
  ) {
    this.registrationForm.controls[key].setValue(event.option.value);
    this.validateForm();
  }

  /**
   * @method handleSelectedValue
   * @param selectedOption object
   * @description Handle selected option
   */
  public handleSelectedValue(selectedOption: {
    from: string;
    to: string;
    value: CmpSchoolMiniObject;
    content: string;
    size: string;
    showDropdown: boolean;
  }) {
    if (selectedOption.from === "custom") {
      this.showFauxDropdown = false;
      this.schoolError = false;
      this.selectedInstitutionId = 0;
      // this.helperService.changeFormField(this.registrationForm, 'customInstitutionName', false, null);
      // this.helperService.changeFormField(this.registrationForm, 'selectedInstitutionId', false, null, true, []);
      this.controlsObj.isCustomSchool = true;
    } else if (
      selectedOption.from === "option" &&
      selectedOption.value === null &&
      selectedOption.content?.length === 0
    ) {
      this.controlsObj.isCustomSchool = false;
      this.helperService.changeFormField(
        this.registrationForm,
        "selectedInstitutionId",
        true,
        null,
      );
    } else {
      this.controlsObj.isCustomSchool = false;
      this.selectedInstitutionId = selectedOption.value?.id
        ? selectedOption.value.id
        : 0;
      this.helperService.changeFormField(
        this.registrationForm,
        "customInstitutionName",
        false,
        null,
        true,
        [],
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "selectedInstitutionId",
        true,
        this.selectedInstitutionId,
      );
      this.registrationForm.controls["selectedInstitutionId"].markAsDirty();
      this.schoolError = this.selectedInstitutionId === 0;
    }
    if (selectedOption.from === "option") {
      this.showSpace = selectedOption.showDropdown ?? false;
      this.spaceSize = selectedOption.size ?? "0rem";
    }
    this.validateForm();
  }

  /**
   * @method setDefaultConsumerType
   * @description Assign value based on consumer type change
   * @returns void
   */
  public setDefaultConsumerType(event: any) {
    this.defaultConsumerType = event.option.value;
    this.registrationForm.controls["defaultConsumerType"].setValue(
      this.defaultConsumerType,
    );
    if (this.defaultConsumerType === "camper") {
      this.helperService.changeFormField(
        this.registrationForm,
        "address",
        false,
        null,
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "city",
        false,
        null,
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "state",
        false,
        null,
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "postalCode",
        false,
        null,
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "adult",
        false,
        "",
      );

      this.helperService.changeFormField(
        this.registrationForm,
        "staffingSeasonId",
        true,
        "",
        true,
        [],
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "institutionType",
        true,
        null,
        true,
        [],
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "staffCellPhone",
        true,
        null,
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "termsAndCondition",
        false,
        null,
        true,
        [],
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "selectedInstitutionId",
        false,
        null,
        true,
        [],
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "applicationTypeId",
        true,
        null,
        true,
        [],
      );
    } else {
      this.helperService.changeFormField(
        this.registrationForm,
        "address",
        true,
        null,
        true,
        [],
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "city",
        true,
        null,
        true,
        [],
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "state",
        true,
        "",
        true,
        [],
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "postalCode",
        true,
        null,
        true,
        [],
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "adult",
        true,
        null,
        true,
        [],
      );
      this.registrationForm.controls["homePhone"].setValue(null);
      this.registrationForm.controls["cellPhone"].setValue(null);
      this.helperService.changeFormField(
        this.registrationForm,
        "staffingSeasonId",
        this.staffingSeasons.length === 1 ? true : false,
        this.staffingSeasons.length === 1
          ? this.staffingSeasons[0].staffingSeasonId
          : "",
      );
      if (this.staffingSeasons.length === 1) {
        this.changeStaffingSeason(this.staffingSeasons[0].staffingSeasonId);
      }
      this.helperService.changeFormField(
        this.registrationForm,
        "applicationTypeId",
        true,
        null,
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "selectedInstitutionId",
        false,
        null,
      );
      this.getSchools();
      this.schoolError = false;
    }
    this.registrationForm.controls["confirmationPrompt"].setValue("");
    this.helperService.changeFormField(
      this.registrationForm,
      "country",
      true,
      "United States",
    );
    this.countriesList.label.selectedLabel = "United States";
    this.statesList.label.selectedLabel = "";
    this.addRemoveClassMultiple("blur", [
      "address",
      "city",
      "state",
      "postalCode",
      "staffingSeasonId",
      "institutionType",
      "staffCellPhone",
      "cellPhone",
      "homePhone",
    ]);

    this.registrationForm.markAsPristine();
    this.registrationForm.markAsUntouched();
    this.validateForm();
  }

  /**
   * @method changeStaffingSeason
   * @param event any
   * @description it will change the staffing season
   * @returns void
   */
  public changeStaffingSeason(event: any) {
    this.registrationForm.controls["staffingSeasonId"].setValue(event);
    this.helperService.changeFormField(
      this.registrationForm,
      "institutionType",
      true,
      "",
      true,
      [],
    );
    this.helperService.changeFormField(
      this.registrationForm,
      "applicationTypeId",
      true,
      null,
    );
    this.helperService.changeFormField(
      this.registrationForm,
      "selectedInstitutionId",
      true,
      null,
    );
    this.helperService.changeFormField(
      this.registrationForm,
      "customInstitutionName",
      true,
      "",
    );
    this.helperService.changeFormField(
      this.registrationForm,
      "staffCellPhone",
      true,
      "",
    );
    this.applicationTypesDropDown.label.selectedLabel = "";
    this.addRemoveClassMultiple("blur", [
      "institutionType",
      "applicationTypeId",
      "selectedInstitutionId",
      "staffCellPhone",
    ]);
    this.registrationForm.markAsPristine();
    this.registrationForm.markAsUntouched();
    this.registrationForm.controls["confirmationPrompt"].setValue("");
    this.getApplicationTypes(event);
  }

  /**
   * @method changeInstitutionType
   * @param event any
   * @description it will change the institution type
   * @returns void
   */
  private changeInstitutionType(applicationTypeId: number) {
    const selectedApplicationType = this.staffApplicationTypes.find(
      (item: any) => item.id === applicationTypeId,
    );
    this.registrationForm.controls["institutionType"].setValue("work");
    if (selectedApplicationType !== undefined) {
      if (selectedApplicationType.schoolSelection) {
        this.registrationForm.controls["institutionType"].setValue("college");
        this.options.label =
          selectedApplicationType.schoolSelectionPlaceholder ??
          "College/University";
      }
    }
    const institutionType =
      this.registrationForm.controls["institutionType"].value;
    if (!selectedApplicationType.schoolSelection) {
      this.helperService.changeFormField(
        this.registrationForm,
        "selectedInstitutionId",
        true,
        null,
      );
      this.helperService.changeFormField(
        this.registrationForm,
        "customInstitutionName",
        true,
        "",
      );
    }
    this.showFauxDropdown =
      institutionType === "college" || institutionType === "work";
    this.selectedInstitutionId =
      institutionType === "college" ? this.selectedInstitutionId : null;
    this.validateForm();
  }

  /**
   * @method closeAlert
   * @description closes the alert modal
   * @returns void
   */
  public closeAlert() {
    this.showAlertModal = false;
  }

  /**
   * @method addRemoveClassMultiple
   * @description it will add or remove the class
   * @returns void
   */
  private addRemoveClassMultiple(action = "", element: any) {
    element.forEach((field: string) => {
      this.addRemoveClass(this.registrationForm, action, field);
    });
  }

  /**
   * @method getTermsAndConditions
   * @description it will used to get the terms and condition
   * @returns void
   */
  private getTermsAndConditions() {
    this.layoutService.getTermsAndConditions().then((response) => {
      if (response) {
        if (
          response.termsAndConditionContentSource === "text" ||
          response.termsAndConditionContentSource === "custom_url"
        ) {
          this.termsAndCondition.show = true;
          this.termsAndCondition.choice =
            response.termsAndConditionContentSource;
          this.termsAndCondition.value = response.settingValue;
        } else if (!response.termsAndConditionContentSource) {
          this.termsAndCondition.show = false;
        }
      }
    });
  }

  /**
   * @method openTermsAndConditionModal
   * @description it will open the terms and condition modal
   * @returns void
   */
  public openTermsAndConditionModal() {
    if (this.termsAndCondition.choice === "text") {
      this.termsAndCondition.alert = true;
      this.alertModalTermsAndCondition = new CmpAlertModalObject();
      this.alertModalTermsAndCondition.title = "Terms & Conditions";
      this.alertModalTermsAndCondition.buttonName = "Close";
      this.alertModalTermsAndCondition.buttonAction = "Close";
      this.alertModalTermsAndCondition.content = String(
        this.termsAndCondition.value,
      );
    } else if (this.termsAndCondition.choice === "custom_url") {
      // open url in another page.
      window.open(String(this.termsAndCondition.value), "_blank");
    }
  }

  /**
   * @method switchSearch
   * @description it will change the validation on switch back to search
   * @returns void
   */
  public switchSearch() {
    this.controlsObj.isCustomSchool = false;
    this.showFauxDropdown = true;
    this.registrationForm.controls.customInstitutionName.setValue("");
    this.registrationForm.controls.customInstitutionName.setErrors(null);
    this.helperService.changeFormField(
      this.registrationForm,
      "selectedInstitutionId",
      true,
      null,
    );
    this.validateForm();
  }

  /**
   * @method getApplicationTypes
   * @description Load Application Types for the given id
   */
  private getApplicationTypes(id: number) {
    this._subSink.add(
      this.apiService
        .get(
          `${this.routerService.backendApiUrl.staffApplicationTypes}/${id}`,
          { notHiredInPreviousSeason: true },
        )
        .subscribe((response) => {
          if (!response.hasErrors) {
            this.staffApplicationTypes = response.data;
            this.loading.applicationTypes = true;
            if (this.staffApplicationTypes.length === 1) {
              this.helperService.changeFormField(
                this.registrationForm,
                "applicationTypeId",
                true,
                this.staffApplicationTypes[0].id,
              );
              this.changeInstitutionType(+this.staffApplicationTypes[0].id);
            } else {
              const options: Array<CmpFauxMetaDropDownOptionsObject> = [];
              this.staffApplicationTypes.forEach((item) => {
                const option = new CmpFauxMetaDropDownOptionsObject();
                option.value = item.id;
                option.optionDesc = item.description;
                option.name = item.name;
                options.push(option);
              });
              this.applicationTypesDropDown.options = options;
              this.applicationTypesDropDown.disabled = false;
              this.applicationTypesDropDown.label.name = "I'm applying for...";
              this.applicationTypesDropDown.label.defaultText =
                "I'm applying for...";
              this.loading.applicationTypes = false;
            }
            window.scrollTo({
              left: 0,
              top: document.body.scrollHeight,
              behavior: "smooth",
            });
          }
        }),
    );
  }

  /**
   * @method setApplicationType
   * @description Emitter response of Application Type
   */
  public setApplicationType(
    selectResponse: { option: CmpFauxMetaDropDownOptionsObject } | null = null,
  ) {
    this.registrationForm.controls["confirmationCheckbox"].setValue(false);
    this.staffApplicationTypes.map((obj) => {
      if (
        obj.confirmationPrompt !== null &&
        obj.id === selectResponse.option.value
      ) {
        selectResponse.option.confirmationPrompt = obj.confirmationPrompt;
      }
    });
    this.registrationForm.controls["applicationTypeId"].setValue(
      selectResponse.option.value,
    );
    this.registrationForm.controls["confirmationPrompt"].setValue(
      selectResponse.option.confirmationPrompt,
    );

    this.changeInstitutionType(+selectResponse.option.value);
    setTimeout(() => {
      this.registrationForm.controls.customInstitutionName.markAsUntouched();
      this.registrationForm.controls.customInstitutionName.markAsPristine();
      this.registrationForm.controls.selectedInstitutionId.markAsUntouched();
      this.registrationForm.controls.selectedInstitutionId.markAsPristine();
      this.options.isInvalid = false;
    });
  }

  /**
   * @method changeInstitutionValidation
   * @description change Institution Validation
   */
  public changeInstitutionValidation(changeInstitutionValidation = false) {
    this.controlsObj.isAttendingSchool = !changeInstitutionValidation;
    this.disableSchoolSearchField = !this.disableSchoolSearchField;
    this.options.disableSearchDropdown = !this.options.disableSearchDropdown;
    this.options.clearInput = !this.options.clearInput;
    if (changeInstitutionValidation) {
      this.registrationForm.controls["selectedInstitutionId"].markAsPristine();
      this.registrationForm.controls["selectedInstitutionId"].markAsUntouched();
      this.selectedInstitutionId = null;
      this.schoolError = false;
    }
    this.validateForm();
  }

  /**
   * @method checkDeviceType
   * @description it will get the current window device type
   * @returns deviceType
   */
  public checkDeviceType() {
    setTimeout(() => {
      // this.isMobile =  this.deviceService.isMobile();
      this.isMobile = window.innerWidth < 768;
    }, 100);
  }
}
